import React, { useState, useEffect, useCallback } from 'react';
import { taskLists } from '../../api';
import TaskListView from './TaskListView';
import './Tasks.css';

const REFRESH_MS = 30 * 1000;

const when = (d) => new Date(d).toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

// Coach: task lists from the head coach, answered with a tick, cross or question mark. An answer can be changed once.
const CoachTasks = ({ onUnansweredChange }) => {
  const [lists, setLists] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [dialogId, setDialogId] = useState(''); // list opened from a notification tap

  const load = useCallback(() => {
    return taskLists.getInbox()
      .then(res => {
        const all = res.data.lists || [];
        setLists(all);
        if (onUnansweredChange) onUnansweredChange(all.reduce((n, l) => n + l.items.filter(i => !i.response).length, 0));
      })
      .catch(() => setLists(prev => prev || []));
  }, [onUnansweredChange]);

  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Tapping a task list notification lands here with that list's id: open it in a dialog
  useEffect(() => {
    const open = () => {
      let id = '';
      try { id = sessionStorage.getItem('focusTaskList') || ''; sessionStorage.removeItem('focusTaskList'); } catch (e) { /* ignore */ }
      if (id) { setDialogId(id); load(); }
    };
    open();
    window.addEventListener('focusTaskList', open);
    return () => window.removeEventListener('focusTaskList', open);
  }, [load]);

  const answer = async (list, item, response) => {
    if (busyKey || item.response === response) return;
    // Changing an answer uses up the one change allowed, so ask first
    if (item.response && !window.confirm('Change your answer? You can only do this once.')) return;
    setBusyKey(`${list._id}:${item._id}`);
    setError('');
    try {
      await taskLists.respond(list._id, item._id, response);
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not send your answer. Try again.');
      load();
    } finally {
      setBusyKey('');
    }
  };

  const card = (list) => {
    const open = list.items.filter(i => !i.response).length;
    return (
      <div key={list._id} className={`tl-card${open === 0 ? ' done' : ''}`}>
        <div className="tl-head">
          <h4 className="tl-title">{list.title || 'Task list'}</h4>
          <span className="tl-meta">{list.fromName} · {when(list.createdAt)}</span>
        </div>
        <TaskListView list={list} mode="coach" busyKey={busyKey} onAnswer={answer} />
      </div>
    );
  };

  const dialogList = dialogId && lists ? lists.find(l => l._id === dialogId) : null;

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '16px' }}>
      <h2 style={{ margin: '0 0 12px 0', fontSize: '20px' }}>Tasks</h2>
      {error && <p className="tl-error">{error}</p>}
      {lists === null ? (
        <p className="tl-meta">Loading...</p>
      ) : lists.length === 0 ? (
        <div className="tl-empty">No task lists from your head coach yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>{lists.map(card)}</div>
      )}

      {dialogId && (
        <div className="tl-dialog-back" onClick={() => setDialogId('')}>
          <div className="tl-dialog" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            {!dialogList ? (
              <p className="tl-meta" style={{ margin: 0 }}>{lists === null ? 'Loading...' : 'This task list is no longer available.'}</p>
            ) : (
              <>
                <div className="tl-head">
                  <h4 className="tl-title">{dialogList.title || 'Task list'}</h4>
                  <span className="tl-meta">{dialogList.fromName}</span>
                </div>
                <TaskListView list={dialogList} mode="coach" busyKey={busyKey} onAnswer={answer} />
              </>
            )}
            {error && <p className="tl-error">{error}</p>}
            <button type="button" className="cancel-button" style={{ width: '100%', marginTop: '14px' }} onClick={() => setDialogId('')}>
              {dialogList && dialogList.items.some(i => !i.response) ? 'Answer later' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachTasks;
